"use client"
import { type CSSProperties, type ReactNode, useCallback, useEffect, useState } from "react"
import { HudGlobeBlock, useHudContext } from "../../shared/playbookHUDInfra"
import { useBandHudOverlay } from "../../../extrapolated-graph/BandHudOverlayChannel"
import { PrimaryGraph } from "../graph/PrimaryGraph"
import { YearJumpMenu } from "../graph/YearJumpMenu"
import {
  AboveGlobeBandHudOverlay,
} from "../../../extrapolated-graph/AboveGlobeBandHudOverlay"
import {
  BelowGlobeBandHudOverlay,
} from "../../../extrapolated-graph/BelowGlobeBandHudOverlay"
import {
  BandHudOverlayProvider,
} from "../../../extrapolated-graph/BandHudOverlayChannel"
import type { RiskFoomDesignId, RiskFoomPreviewMode, RiskAmbientDesignId, RiskSpaceExplorerMode, RiskTedGuideMode } from "../../../extrapolated-graph/RiskFoomPreviewOverlay"
import { useMetricDetails } from "../../metric-details/metricDetailsContext"

// The earth sat a touch too high in the graph's takeover-risk dither field;
// Joanna's 2026-07-13 placement pass settled on 5px lower (was -23).
const GLOBE_TOP_OFFSET_PX = -18
// The globe block's stock geometry: PlaybookGlobe's default canvas size and
// the anchor the className below pins (-left-[38px]). A variant passing
// `globeSize` grows the canvas around its CENTER — the anchor shifts by half
// the size delta so the earth stays put while it scales.
const GLOBE_DEFAULT_SIZE_PX = 400
const GLOBE_LEFT_OFFSET_PX = -38
// Picks tab 6 (globeFieldHoverFade): how long the earth takes to ease to the
// fade opacity while the risk dither is hovered, and back.
const GLOBE_FIELD_HOVER_FADE_MS = 200
// Stacking order: graph body (in its own isolated context) < below-globe band
// highlight layer (z-5) < globe (z-10) < above-globe label/tooltip layer
// (z-20). Both overlay layers must be SIBLINGS of the graph container (not
// children) to escape the graph's stacking context (`isolate`); see the
// placement comments below. The band highlight raster sits below the globe (so
// the globe paints over it), while the small text annotations sit above it.
const BELOW_GLOBE_BAND_LAYER_Z_INDEX = 5
const ABOVE_GLOBE_LABEL_LAYER_Z_INDEX = 20
// The year-jump menu the current-year pill toggles opens over the globe, so
// its layer stacks above everything else in the block.
const YEAR_JUMP_MENU_LAYER_Z_INDEX = 30

// The persistent globe + extrapolated graph block that most HUD variants
// include in their body. Variants that don't want a globe/graph (e.g. the
// pure-text TextHudBody variant) simply omit this. Optional `children`
// render inside the sticky graph container, alongside <PrimaryGraph />.
//
// While a metric-details state is open, the whole variant layer (this block
// included) fades and hides via the layer crossfade in HudShellContainer —
// the block itself never unmounts, so its transitions play fully both ways;
// the globe and graph are paused so they cost nothing while the layer is
// hidden.
//
// The props are per-variant graph restyle knobs, forwarded to
// <PrimaryGraph /> — see the HUD 87 usage in variants.tsx.
export const HudPersistentGlobeAndGraph = ({
  children,
  currentYearPill,
  showAICapabilitiesLabel,
  capabilityPastLineColor,
  capabilityPastLineStrokeWidth,
  clipLinesToDitherTop,
  capabilityLineFloorValue,
  showCurrentPointMarker,
  currentPointMarkerRadius,
  showCurrentPointCapabilityLabel,
  minimumGraphDate,
  hideGraphExplainerTooltip,
  pointHoverMaxDistanceToLinePx,
  takeoverRiskAreaHover,
  riskFoomPreview,
  riskFoomDesign,
  riskFoomTedCaption,
  riskFoomOnPointHover,
  riskSpaceExplorer,
  riskTedGuide,
  riskAmbientDisplay,
  riskHoverDimDisabled,
  preChoiceYAxisMax,
  graphLogScale,
  highlightLineWhilePointHovered,
  riskZoneDimTween,
  riskZoneDimInverted,
  riskZoneDimDotShrink,
  riskZoneContinuousGuide,
  pointGuideTedDash,
  globeSize,
  globeFieldHoverFade,
}: {
  children?: ReactNode
  currentYearPill?: boolean
  showAICapabilitiesLabel?: boolean
  capabilityPastLineColor?: string
  capabilityPastLineStrokeWidth?: number
  // Clips the capability line to the red dither field's top edge (tab 49) —
  // see clipLinesToDitherTop in extrapolated-graph/types.ts.
  clipLinesToDitherTop?: boolean
  capabilityLineFloorValue?: number
  showCurrentPointMarker?: boolean
  currentPointMarkerRadius?: number
  showCurrentPointCapabilityLabel?: boolean
  minimumGraphDate?: Date
  // See PlaybookExtrapolatedGraph's hideGraphExplainerTooltip prop.
  hideGraphExplainerTooltip?: boolean
  // See ExtrapolatedGraphRenderProps.pointHoverMaxDistanceToLinePx.
  pointHoverMaxDistanceToLinePx?: number
  // See ExtrapolatedGraphRenderProps.takeoverRiskAreaHover.
  takeoverRiskAreaHover?: boolean
  // See ExtrapolatedGraphRenderProps.riskFoomPreview.
  riskFoomPreview?: RiskFoomPreviewMode
  // See ExtrapolatedGraphRenderProps.riskFoomDesign.
  riskFoomDesign?: RiskFoomDesignId
  // See ExtrapolatedGraphRenderProps.riskFoomTedCaption.
  riskFoomTedCaption?: boolean
  // See ExtrapolatedGraphRenderProps.riskFoomOnPointHover.
  riskFoomOnPointHover?: boolean
  // See ExtrapolatedGraphProps.riskSpaceExplorer (the 2D risk-space explorer).
  riskSpaceExplorer?: RiskSpaceExplorerMode
  // See ExtrapolatedGraphProps.riskTedGuide (the tab-26 TED guide).
  riskTedGuide?: RiskTedGuideMode
  // See ExtrapolatedGraphProps.riskAmbientDisplay (the always-on readout).
  riskAmbientDisplay?: RiskAmbientDesignId
  // See ExtrapolatedGraphProps.riskHoverDimDisabled (tab 53: no field dim).
  riskHoverDimDisabled?: boolean
  // See PlaybookExtrapolatedGraph.preChoiceYAxisMax (tab 33 close-up zoom).
  preChoiceYAxisMax?: number
  // See PlaybookExtrapolatedGraph.useLogScale (tab 33 pairs it with the
  // zoomed-out close-up so the line keeps a readable curve).
  graphLogScale?: boolean
  // See ExtrapolatedGraphRenderProps.highlightLineWhilePointHovered.
  highlightLineWhilePointHovered?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimTween (tabs 66–70).
  riskZoneDimTween?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimInverted (tab 70).
  riskZoneDimInverted?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimDotShrink (picks tab 2).
  riskZoneDimDotShrink?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneContinuousGuide (tab 68).
  riskZoneContinuousGuide?: boolean
  // See ExtrapolatedGraphRenderProps.pointGuideTedDash.
  pointGuideTedDash?: boolean
  // The globe's canvas size in px (PlaybookGlobe default 400). Grows/shrinks
  // around the earth's center — see GLOBE_DEFAULT_SIZE_PX above.
  globeSize?: number
  // Picks tab 6: the earth's opacity while the risk dither is hovered (0.5 =
  // half strength), easing over GLOBE_FIELD_HOVER_FADE_MS both ways. Unset =
  // no fade.
  globeFieldHoverFade?: number
}) => {
  const ctx = useHudContext()
  const { epilogue } = ctx
  const { openId } = useMetricDetails()
  const dismissed = openId !== null

  // The year pill (currentYearPill mode) doubles as a year table-of-contents
  // trigger: clicking it toggles this menu, rendered in its own layer below.
  // Stable callbacks — the toggle threads through the memoized graph.
  const [yearJumpMenuOpen, setYearJumpMenuOpen] = useState(false)
  const toggleYearJumpMenu = useCallback(() => setYearJumpMenuOpen(open => !open), [])
  const closeYearJumpMenu = useCallback(() => setYearJumpMenuOpen(false), [])
  // The menu only makes sense in the normal reading state: the epilogue
  // restyles the pill into a plain readout, and an open metric-details
  // state hides the whole variant layer.
  useEffect(() => {
    if (epilogue.isEpilogueHudMode || dismissed) setYearJumpMenuOpen(false)
  }, [epilogue.isEpilogueHudMode, dismissed])

  // The globe's OUTER block keeps its absolute anchor and the galaxy-hide
  // fade (its own opacity transition); the epilogue move transform rides the
  // INNER wrapper (innerStyle) so the two transitions never fight over
  // `transition`. Epilogue mode drops the galaxy-hide styles, matching the
  // pre-existing behavior (the 2042 camera zoom-away hides the globe there).
  // A non-default globeSize shifts the anchor by half the delta (both axes)
  // so the earth scales in place; the inline `left` overrides the
  // className's stock -left-[38px].
  const globeSizeDeltaPx = (globeSize ?? GLOBE_DEFAULT_SIZE_PX) - GLOBE_DEFAULT_SIZE_PX
  const globeOuterStyle = {
    ...(epilogue.isEpilogueHudMode ? {} : ctx.galaxyHideStyle),
    // While the choose-your-path block is on screen the earth fades out so
    // the graph's all-plans fan reads unobstructed — riding galaxyHideStyle's
    // own 600ms opacity transition. Never fights the epilogue styles: the
    // choice point sits far above the epilogue's scroll range.
    ...(ctx.isChoicePointOnScreen ? { opacity: 0 } : {}),
    pointerEvents: 'none' as const,
    top: GLOBE_TOP_OFFSET_PX - globeSizeDeltaPx / 2,
    ...(globeSize !== undefined
      ? { left: GLOBE_LEFT_OFFSET_PX - globeSizeDeltaPx / 2 }
      : {}),
  }

  // Constant class/positioning in and out of the epilogue: the graph
  // container stays in flow and moves purely via epilogue.graphContainerStyle's
  // transform, so entering, leaving, and reversing mid-transition all glide.
  // `isolate` keeps it a stacking context (it used to be `sticky`, which
  // forced one) so its z-indexed children stay trapped below the overlay
  // layers and the globe.
  const graphContainerClassName = "relative isolate font-mono text-[12px] w-full"
  const graphContainerStyle = epilogue.graphContainerStyle

  return (
    <BandHudOverlayProvider>
      {/* Above-globe label layer. It must live OUTSIDE the graph container:
          the graph container is a stacking context (`isolate`), so anything
          inside it is trapped below the globe (z-10). As a sibling, this
          layer's own z-20 is compared against the globe at the parent level
          and wins — painting the hovered-band highlight + danger label above
          the globe.

          It uses the EXACT same positioning as the graph container (same
          class + style), so it tracks the graph through the epilogue
          transform in perfect lockstep with zero JS — no portal, no fixed
          positioning, no per-frame re-measure. It is rendered BEFORE the
          graph container and forced to zero height so it contributes no flow
          space: that makes its top edge coincide with the graph container's
          top edge, so its children — positioned at graph-local coordinates —
          overlay the graph exactly. */}
      <div
        className={`${graphContainerClassName} pointer-events-none`}
        style={{
          ...graphContainerStyle,
          height: 0,
          zIndex: ABOVE_GLOBE_LABEL_LAYER_Z_INDEX,
        }}
      >
        <AboveGlobeBandHudOverlay />
      </div>
      {/* Below-globe band highlight layer. Same pinned-to-graph positioning as
          the above-globe layer, but at a z-index below the globe so the globe
          paints on top of the hovered-band raster. Like the above-globe layer
          it must be a SIBLING of the graph container (not a child) so its
          z-index is compared against the globe at the parent level. Forced to
          zero height so it contributes no flow space, making its top edge
          coincide with the graph container's top edge. */}
      <div
        className={`${graphContainerClassName} pointer-events-none`}
        style={{
          ...graphContainerStyle,
          height: 0,
          zIndex: BELOW_GLOBE_BAND_LAYER_Z_INDEX,
        }}
      >
        <BelowGlobeBandHudOverlay />
      </div>
      {/* Year-jump menu layer: same zero-height pinned-to-the-graph's-box
          positioning as the band overlay layers above — like them it must
          render BEFORE the in-flow graph container so its top edge coincides
          with the graph's — stacked over the globe so the open menu isn't
          trapped under it. The layer itself is pointer-inert; the menu
          re-enables pointer events on itself. */}
      {yearJumpMenuOpen && (
        <div
          className={`${graphContainerClassName} pointer-events-none`}
          style={{
            ...graphContainerStyle,
            height: 0,
            zIndex: YEAR_JUMP_MENU_LAYER_Z_INDEX,
          }}
        >
          <YearJumpMenu onClose={closeYearJumpMenu} />
        </div>
      )}
      {globeFieldHoverFade !== undefined ? (
        <HudGlobeBlockWithFieldFade
          fadeOpacity={globeFieldHoverFade}
          containerRef={epilogue.globeContainerRef}
          className="absolute -left-[38px] flex items-start z-[10] pointer-events-none"
          style={globeOuterStyle}
          innerStyle={epilogue.globeContainerStyle}
          size={globeSize}
          paused={dismissed}
        />
      ) : (
        <HudGlobeBlock
          containerRef={epilogue.globeContainerRef}
          className="absolute -left-[38px] flex items-start z-[10] pointer-events-none"
          style={globeOuterStyle}
          innerStyle={epilogue.globeContainerStyle}
          size={globeSize}
          paused={dismissed}
        />
      )}
      <div
        ref={epilogue.graphFlowContainerRef}
        className={graphContainerClassName}
        style={graphContainerStyle}
      >
        <PrimaryGraph
          currentYearPill={currentYearPill}
          onCurrentYearPillClick={currentYearPill ? toggleYearJumpMenu : undefined}
          currentYearPillMenuOpen={yearJumpMenuOpen}
          showAICapabilitiesLabel={showAICapabilitiesLabel}
          capabilityPastLineColor={capabilityPastLineColor}
          capabilityPastLineStrokeWidth={capabilityPastLineStrokeWidth}
          clipLinesToDitherTop={clipLinesToDitherTop}
          capabilityLineFloorValue={capabilityLineFloorValue}
          showCurrentPointMarker={showCurrentPointMarker}
          currentPointMarkerRadius={currentPointMarkerRadius}
          showCurrentPointCapabilityLabel={showCurrentPointCapabilityLabel}
          minimumGraphDate={minimumGraphDate}
          hideGraphExplainerTooltip={hideGraphExplainerTooltip}
          pointHoverMaxDistanceToLinePx={pointHoverMaxDistanceToLinePx}
          takeoverRiskAreaHover={takeoverRiskAreaHover}
          riskFoomPreview={riskFoomPreview}
          riskFoomDesign={riskFoomDesign}
          riskFoomTedCaption={riskFoomTedCaption}
          riskFoomOnPointHover={riskFoomOnPointHover}
          riskSpaceExplorer={riskSpaceExplorer}
          riskTedGuide={riskTedGuide}
          riskAmbientDisplay={riskAmbientDisplay}
          riskHoverDimDisabled={riskHoverDimDisabled}
          preChoiceYAxisMax={preChoiceYAxisMax}
          graphLogScale={graphLogScale}
          highlightLineWhilePointHovered={highlightLineWhilePointHovered}
          riskZoneDimTween={riskZoneDimTween}
          riskZoneDimInverted={riskZoneDimInverted}
          riskZoneDimDotShrink={riskZoneDimDotShrink}
          riskZoneContinuousGuide={riskZoneContinuousGuide}
          pointGuideTedDash={pointGuideTedDash}
          paused={dismissed}
        />
        {children}
      </div>
    </BandHudOverlayProvider>
  )
}

// The globeFieldHoverFade wrapper (picks tab 6): reads the field-hover state
// off the band channel — so it must render INSIDE the provider above, which
// is why HudPersistentGlobeAndGraph itself can't do this — and eases the
// earth to `fadeOpacity` while the risk dither is hovered. The fade rides
// the OUTER block's style: it multiplies the galaxy-hide opacity (0 or 1)
// and replaces its transition with the fade clock, so while this mode is on
// the galaxy hide fades at GLOBE_FIELD_HOVER_FADE_MS instead of its usual
// 600ms — acceptable on an exploration tab.
const HudGlobeBlockWithFieldFade = ({
  fadeOpacity,
  style,
  ...globeProps
}: {
  fadeOpacity: number
  className?: string
  style: CSSProperties
  containerRef?: React.MutableRefObject<HTMLDivElement | null>
  innerStyle?: CSSProperties
  size?: number
  paused?: boolean
}) => {
  const { takeoverRiskAreaHover } = useBandHudOverlay()
  const baseOpacity = typeof style.opacity === "number" ? style.opacity : 1
  return (
    <HudGlobeBlock
      {...globeProps}
      style={{
        ...style,
        opacity: takeoverRiskAreaHover ? baseOpacity * fadeOpacity : baseOpacity,
        transition: `opacity ${GLOBE_FIELD_HOVER_FADE_MS}ms ease-out, visibility 0s`,
      }}
    />
  )
}
